import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import type {
  CompanySearchResult,
  DirectoryPerson,
} from "@sales-ops-amplify/shared";
import {
  purgeAccount,
  purgeDeletedAccounts,
  restoreAccount,
} from "@/api/accounts";
import { searchCompanies } from "@/api/companies";
import {
  purgeDeletedLeads,
  purgeLead,
  restoreLead,
} from "@/api/leads";
import { searchPeople } from "@/api/people";
import {
  SettingsPageHeader,
  SettingsSection,
  SettingsTable,
} from "@/components/settings/settings-ui";
import { cn } from "@/cn";
import { workspaceAccountDetailId } from "@/lib/workspace-account";

type BinEntity = "accounts" | "leads";

const PER_PAGE = 25;

function entityFromPath(pathname: string): BinEntity {
  if (pathname.includes("/recycling-bin/leads")) return "leads";
  return "accounts";
}

export function SettingsRecyclingBinPage() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const entity = entityFromPath(pathname);

  if (
    pathname === "/settings/recycling-bin" ||
    pathname === "/settings/recycling-bin/"
  ) {
    return <Navigate to="/settings/recycling-bin/accounts" replace />;
  }

  return (
    <RecyclingBinContent
      entity={entity}
      onEntityChange={(next) =>
        navigate(`/settings/recycling-bin/${next}`, { replace: true })
      }
    />
  );
}

function RecyclingBinContent({
  entity,
  onEntityChange,
}: {
  entity: BinEntity;
  onEntityChange: (entity: BinEntity) => void;
}) {
  const [page, setPage] = useState(1);
  const [accounts, setAccounts] = useState<CompanySearchResult[]>([]);
  const [leads, setLeads] = useState<DirectoryPerson[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(
    async (nextPage = page) => {
      setLoading(true);
      setError(null);
      try {
        if (entity === "accounts") {
          const res = await searchCompanies({
            scope: "account",
            deletedOnly: true,
            page: nextPage,
            perPage: PER_PAGE,
          });
          setAccounts(res.companies);
          setTotalEntries(res.totalEntries);
          setLeads([]);
        } else {
          const res = await searchPeople({
            scope: "lead",
            deletedOnly: true,
            page: nextPage,
            perPage: PER_PAGE,
          });
          setLeads(res.people);
          setTotalEntries(res.totalEntries);
          setAccounts([]);
        }
        setPage(nextPage);
        setSelectedIds(new Set());
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load recycling bin");
      } finally {
        setLoading(false);
      }
    },
    [entity, page],
  );

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when entity changes
  }, [entity]);

  const visibleIds = useMemo(() => {
    if (entity === "accounts") {
      return accounts
        .map((c) => workspaceAccountDetailId(c))
        .filter((id): id is string => !!id);
    }
    return leads
      .map((p) => p.leadId)
      .filter((id): id is string => !!id);
  }, [accounts, leads, entity]);

  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(visibleIds));
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const restoreMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (entity === "accounts") {
        for (const id of ids) await restoreAccount(id);
      } else {
        for (const id of ids) await restoreLead(id);
      }
      return ids.length;
    },
    onSuccess: (n) => {
      setMessage(
        `Restored ${n} ${entity === "accounts" ? "Account" : "Lead"}${n === 1 ? "" : "s"}`,
      );
      void load(page);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Restore failed");
    },
  });

  const purgeMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (entity === "accounts") {
        for (const id of ids) await purgeAccount(id);
      } else {
        for (const id of ids) await purgeLead(id);
      }
      return ids.length;
    },
    onSuccess: (n) => {
      setMessage(
        `Permanently deleted ${n} ${entity === "accounts" ? "Account" : "Lead"}${n === 1 ? "" : "s"}`,
      );
      void load(1);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Permanent delete failed");
    },
  });

  const emptyBinMutation = useMutation({
    mutationFn: () =>
      entity === "accounts" ? purgeDeletedAccounts() : purgeDeletedLeads(),
    onSuccess: (res) => {
      setMessage(
        `Permanently deleted ${res.purged} ${entity === "accounts" ? "Account" : "Lead"}${res.purged === 1 ? "" : "s"}`,
      );
      void load(1);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Empty recycling bin failed");
    },
  });

  const totalPages = Math.max(1, Math.ceil(totalEntries / PER_PAGE));
  const noun = entity === "accounts" ? "Account" : "Lead";
  const nounPlural = entity === "accounts" ? "accounts" : "leads";

  return (
    <div>
      <SettingsPageHeader
        title="Recycling bin"
        description="Restore or permanently delete soft-deleted Accounts and Leads. Items are removed automatically after the retention period."
      />

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-border">
        {(
          [
            ["accounts", "Accounts"],
            ["leads", "Leads"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => onEntityChange(id)}
            className={cn(
              "shrink-0 border-b-2 px-4 py-2.5 text-sm font-medium transition",
              entity === id
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {(message || error) && (
        <div className="mb-4 space-y-2">
          {message && (
            <p className="rounded-md bg-brand-soft px-3 py-2 text-sm text-brand">
              {message}
            </p>
          )}
          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      )}

      <SettingsSection
        title={`${noun}s in recycling bin`}
        description={`${totalEntries.toLocaleString()} soft-deleted ${nounPlural}`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={loading}
              onClick={() => void load(page)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-surface-hover disabled:opacity-50"
            >
              Refresh
            </button>
            <button
              type="button"
              disabled={
                totalEntries === 0 || emptyBinMutation.isPending || loading
              }
              onClick={() => {
                if (
                  !window.confirm(
                    `Permanently delete all ${totalEntries} ${noun}${totalEntries === 1 ? "" : "s"} in the recycling bin? This cannot be undone.`,
                  )
                ) {
                  return;
                }
                emptyBinMutation.mutate();
              }}
              className="rounded-md border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              Empty recycling bin
            </button>
          </div>
        }
      >
        {selectedIds.size > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {allSelected ? "Deselect page" : "Select page"}
            </button>
            <button
              type="button"
              disabled={restoreMutation.isPending}
              onClick={() => restoreMutation.mutate([...selectedIds])}
              className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
            >
              Restore
            </button>
            <button
              type="button"
              disabled={purgeMutation.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `Permanently delete ${selectedIds.size} ${noun}${selectedIds.size === 1 ? "" : "s"}? This cannot be undone.`,
                  )
                ) {
                  return;
                }
                purgeMutation.mutate([...selectedIds]);
              }}
              className="rounded-md border border-destructive/40 px-2.5 py-1.5 text-xs text-destructive disabled:opacity-50"
            >
              Delete permanently
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
        )}

        {!loading && totalEntries > 0 && selectedIds.size === 0 && (
          <div className="mb-3">
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Select page
            </button>
          </div>
        )}

        {loading && totalEntries === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <>
            <SettingsTable
              columns={["", "Name", entity === "accounts" ? "Industry" : "Title", entity === "accounts" ? "Domain" : "Email", "Actions"]}
              empty={`No soft-deleted ${nounPlural}. Deleted items from ${entity === "accounts" ? "Accounts" : "Leads"} appear here.`}
              rows={
                entity === "accounts"
                  ? accounts.flatMap((row) => {
                      const id = workspaceAccountDetailId(row);
                      if (!id) return [];
                      return [
                        {
                          "": (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(id)}
                              onChange={() => toggleOne(id)}
                              aria-label={`Select ${row.name}`}
                            />
                          ),
                          Name: (
                            <span className="font-medium">{row.name}</span>
                          ),
                          Industry: row.industry ?? "—",
                          Domain: row.domain ?? row.websiteUrl ?? "—",
                          Actions: (
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={restoreMutation.isPending}
                                onClick={() => restoreMutation.mutate([id])}
                                className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
                              >
                                Restore
                              </button>
                              <button
                                type="button"
                                disabled={purgeMutation.isPending}
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      `Permanently delete ${row.name}? This cannot be undone.`,
                                    )
                                  ) {
                                    return;
                                  }
                                  purgeMutation.mutate([id]);
                                }}
                                className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                              >
                                Delete permanently
                              </button>
                            </div>
                          ),
                        },
                      ];
                    })
                  : leads.flatMap((row) => {
                      const id = row.leadId;
                      if (!id) return [];
                      return [
                        {
                          "": (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(id)}
                              onChange={() => toggleOne(id)}
                              aria-label={`Select ${row.name}`}
                            />
                          ),
                          Name: (
                            <span className="font-medium">{row.name}</span>
                          ),
                          Title: row.title ?? "—",
                          Email: row.email ?? "—",
                          Actions: (
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={restoreMutation.isPending}
                                onClick={() => restoreMutation.mutate([id])}
                                className="text-xs font-medium text-brand hover:underline disabled:opacity-50"
                              >
                                Restore
                              </button>
                              <button
                                type="button"
                                disabled={purgeMutation.isPending}
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      `Permanently delete ${row.name}? This cannot be undone.`,
                                    )
                                  ) {
                                    return;
                                  }
                                  purgeMutation.mutate([id]);
                                }}
                                className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                              >
                                Delete permanently
                              </button>
                            </div>
                          ),
                        },
                      ];
                    })
              }
            />

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => void load(page - 1)}
                    className="rounded-md border border-border px-2.5 py-1 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages || loading}
                    onClick={() => void load(page + 1)}
                    className="rounded-md border border-border px-2.5 py-1 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </SettingsSection>
    </div>
  );
}
